import Icon from '@rio-cloud/rio-uikit/Icon';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Table, {
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
    TableHeaderRow,
    TableRow,
} from '@rio-cloud/rio-uikit/Table';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import { type ReactNode, useId, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DiscountIdentifier } from '../../common/discount/DiscountService';
import type { AssetDiscount } from '../../common/discount/redux/types';
import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import type { ProductType } from '../../common/product/product';
import { isFreeTrialLevel } from '../../common/termedLevel/freeTrialLevel';
import type { TermedLevel } from '../../common/termedLevel/types';
import { DATE_FORMAT } from '../../common/utils/dateUtil';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { ContractResourceName } from '../contracts/ContractResourceName';
import { ContractTableRowRenewalText } from '../contracts/ContractTableRowRenewalText';
import { useSorting } from '../contracts/useSorting';
import { BookingOverviewRemainingDays } from './BookingOverviewRemainingDays';
import {
    type BookingOverviewColumn,
    type BookingOverviewGrouping,
    bookingOverviewColumns,
    defaultBookingOverviewColumns,
} from './bookingOverviewColumns';
import { getBookingOverviewResourceIcon } from './bookingOverviewResourceIcon';
import { getBookingOverviewStatus } from './bookingOverviewStatus';

interface Props {
    contracts: TermedLevel[];
    assetTypesById: ReadonlyMap<string, string | undefined>;
    grouping?: BookingOverviewGrouping;
    visibleColumns?: BookingOverviewColumn[];
    legacyLevelsByProduct?: ReadonlyMap<string, ReadonlySet<string>>;
    productType: ProductType;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    legacyLevels: ReadonlySet<string>;
    onSelectContract: (contract: TermedLevel) => void;
}

interface Column {
    id: BookingOverviewColumn;
    propertyName: keyof TermedLevel;
    label: ReactNode;
}

function getColumns(productType: ProductType, grouping: BookingOverviewGrouping): Column[] {
    return bookingOverviewColumns.map(column => ({
        id: column.id,
        propertyName: column.id === 'resourceName' && grouping === 'resources' ? 'productName' : column.id,
        label:
            column.id === 'resourceName' ? (
                grouping === 'resources' ? (
                    <FormattedMessage id='intl-msg:marketplace.history.header.service' />
                ) : (
                    <ProductTypedFormattedMessage
                        productType={productType}
                        userBasedId='intl-msg:marketplace.contracts.user.table.header'
                        assetBasedId='intl-msg:marketplace.contracts.asset.table.header'
                    />
                )
            ) : (
                <FormattedMessage id={column.labelId} />
            ),
    }));
}

function ContractPeriod({
    contract,
    discount,
    isLegacy,
}: {
    contract: TermedLevel;
    discount?: AssetDiscount;
    isLegacy: boolean;
}) {
    const intl = useIntl();
    const isFreeTrial = isFreeTrialLevel(contract.level);

    return (
        <div className='display-flex align-items-center gap-5'>
            <span className='label label-condensed label-default white-space-nowrap'>
                {contract.periodLength !== undefined && contract.periodUnit !== undefined
                    ? `${contract.periodLength} ${formatDurationUnit(contract.periodUnit, intl, contract.periodLength)}`
                    : '--'}
            </span>
            {isLegacy && <span className='label label-condensed label-info white-space-nowrap'>Legacy</span>}
            {isFreeTrial && (
                <span className='label label-condensed label-secondary white-space-nowrap'>
                    <FormattedMessage id='intl-msg:marketplace.free.trial' />
                </span>
            )}
            {!isFreeTrial && discount && (
                <span className='label label-condensed label-success white-space-nowrap'>
                    -{Math.round(discount.discountPercentage)}%
                </span>
            )}
        </div>
    );
}

function ContractDate({ value }: { value?: string }) {
    const intl = useIntl();
    return value ? intl.formatDate(parseISO(value), DATE_FORMAT) : <span>–</span>;
}

const getOverviewVariantName = (contract: TermedLevel): string => contract.variantShortName?.trim() ?? '';

function ContractVariant({ contract }: { contract: TermedLevel }) {
    const variantName = getOverviewVariantName(contract);
    return variantName ? (
        <span className='label label-condensed label-primary label-filled'>{variantName}</span>
    ) : (
        <span>–</span>
    );
}

export function BookingOverviewContractState({
    contract,
    showExplanation = false,
}: {
    contract: TermedLevel;
    showExplanation?: boolean;
}) {
    const tooltipId = useId();
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const status = getBookingOverviewStatus(contract);
    const labelStyle = {
        ACTIVE: 'label-success label-filled',
        PENDING: 'label-success',
        EXPIRING: 'label-warning label-filled',
        UNASSIGNED: 'label-default label-filled',
    }[status];
    const className = `label label-condensed text-uppercase ${labelStyle}`;
    const label = <FormattedMessage id={`intl-msg:marketplace.bookings.overview.status.${status.toLowerCase()}`} />;
    if (status !== 'EXPIRING' && status !== 'UNASSIGNED') {
        return <div className={className}>{label}</div>;
    }

    const explanation = (
        <FormattedMessage id={`intl-msg:marketplace.bookings.overview.status.${status.toLowerCase()}.explanation`} />
    );
    if (showExplanation) {
        return (
            <div>
                <div className={className} aria-describedby={tooltipId}>
                    {label}
                </div>
                <div id={tooltipId} className='text-size-12 text-normal text-color-dark margin-top-5'>
                    <span className='uikit-icon icon-info-sign text-size-12 margin-right-5' aria-hidden />
                    {explanation}
                </div>
            </div>
        );
    }

    return (
        <OverlayTrigger
            placement='top'
            show={hovered || focused}
            onToggle={setHovered}
            overlay={
                <Tooltip id={tooltipId} allowOnTouch>
                    {explanation}
                </Tooltip>
            }
        >
            <button
                type='button'
                className={className}
                aria-describedby={hovered || focused ? tooltipId : undefined}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onClick={event => event.stopPropagation()}
                onKeyDown={event => {
                    event.stopPropagation();
                    if (event.key === 'Escape') {
                        setHovered(false);
                        setFocused(false);
                    }
                }}
            >
                {label}
            </button>
        </OverlayTrigger>
    );
}

function ContractTableRow({
    contract,
    assetType,
    findDiscount,
    legacyLevels,
    grouping,
    visibleColumns,
}: {
    contract: TermedLevel;
    assetType: string | undefined;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    legacyLevels: ReadonlySet<string>;
    grouping: BookingOverviewGrouping;
    visibleColumns: BookingOverviewColumn[];
}) {
    const isUnassigned = getBookingOverviewStatus(contract) === 'UNASSIGNED';
    const isDeleted = contract.hasInactiveActivationState;
    const isDisabled = isDeleted && !isUnassigned;
    const discount = findDiscount({
        assetId: contract.resourceId,
        productId: contract.productId,
        productLevel: contract.level,
    });
    const values: Record<BookingOverviewColumn, ReactNode> = {
        resourceName:
            grouping === 'resources' ? (
                contract.productName
            ) : (
                <>
                    <span className='margin-right-5' aria-hidden={true}>
                        <Icon iconName={getBookingOverviewResourceIcon(assetType)} />
                    </span>
                    {isDeleted ? (
                        contract.resourceName || contract.resourceId
                    ) : (
                        <ContractResourceName contract={contract} pendingActivationClassName='text-size-12' />
                    )}
                    {isUnassigned && (
                        <span className='display-block text-size-12 text-color-dark'>
                            <FormattedMessage id='intl-msg:marketplace.bookings.overview.vehicle.unassignedHint' />
                        </span>
                    )}
                </>
            ),
        variantName: <ContractVariant contract={contract} />,
        periodLength: (
            <ContractPeriod contract={contract} discount={discount} isLegacy={legacyLevels.has(contract.level)} />
        ),
        startsAt: <ContractDate value={contract.startsAt} />,
        endsAt: <ContractDate value={contract.endsAt} />,
        remainingDays: <BookingOverviewRemainingDays contract={contract} />,
        contractState: <BookingOverviewContractState contract={contract} />,
        renewsToLevelName: <ContractTableRowRenewalText contract={contract} />,
    };
    return (
        <TableRow
            {...contract}
            id={`${contract.productId}-${contract.resourceType}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
            className={classNames({ 'opacity-50 bg-lightest': isDisabled })}
            disabled={isDisabled}
        >
            {visibleColumns.map(column => (
                <TableColumn
                    key={column}
                    columnKey={column}
                    className='padding-x-15'
                    horizontalAlign={column === 'renewsToLevelName' ? 'right' : 'left'}
                >
                    {values[column]}
                </TableColumn>
            ))}
        </TableRow>
    );
}

export function BookingOverviewContractTable({
    contracts,
    assetTypesById,
    grouping = 'services',
    visibleColumns = defaultBookingOverviewColumns,
    legacyLevelsByProduct,
    productType,
    findDiscount,
    legacyLevels,
    onSelectContract,
}: Props) {
    const { sort, handleSortChange, sortBy, direction } = useSorting<TermedLevel>(
        grouping === 'resources' ? 'productName' : 'resourceName'
    );
    const columns = useMemo(
        () =>
            getColumns(productType, grouping)
                .filter(column => visibleColumns.includes(column.id))
                .sort((a, b) => visibleColumns.indexOf(a.id) - visibleColumns.indexOf(b.id)),
        [productType, grouping, visibleColumns]
    );

    return (
        <Table
            rowKey='id'
            columnOrder={visibleColumns}
            hover
            border={false}
            rounded={false}
            className='rounded-bottom'
            sortBy={columns.find(column => column.propertyName === sortBy)?.id}
            sortDirection={direction}
            onSortChange={columnKey => {
                const column = columns.find(({ id }) => id === columnKey);
                if (column) {
                    handleSortChange(column.propertyName);
                }
            }}
            onRowClick={contract => onSelectContract(contract as unknown as TermedLevel)}
        >
            <TableHeader>
                <TableHeaderRow>
                    {columns.map(column => (
                        <TableHeaderColumn
                            key={column.id}
                            columnKey={column.id}
                            className='padding-x-15'
                            horizontalAlign={column.id === 'renewsToLevelName' ? 'right' : 'left'}
                            width={column.id === 'renewsToLevelName' ? 240 : undefined}
                            sortable
                        >
                            {column.label}
                        </TableHeaderColumn>
                    ))}
                </TableHeaderRow>
            </TableHeader>
            <TableBody maxHeight={290}>
                {sort(contracts).map(contract => (
                    <ContractTableRow
                        key={`${contract.productId}-${contract.resourceType}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
                        contract={contract}
                        assetType={assetTypesById.get(contract.resourceId)}
                        findDiscount={findDiscount}
                        legacyLevels={legacyLevelsByProduct?.get(contract.productId) ?? legacyLevels}
                        grouping={grouping}
                        visibleColumns={visibleColumns}
                    />
                ))}
            </TableBody>
        </Table>
    );
}
